<script>
  import { session } from '../state/session.svelte.js';
  import { generationRun } from '../state/generation-run.svelte.js';
  import { sessionPersistence, refreshSessionStorage, retrySessionSave,
    restoreSavedSession, forgetSavedSession, refreshSavedProjects } from '../state/session-persistence.svelte.js';
  import { projectsNeedingRecovery, unidentifiedSubmissions } from '../utils/session-recovery.js';

  let dialog;
  let confirmingDelete = $state(null);
  let isBusy = $state(false);
  let listLimit = $state(50);
  const uncertainCount = $derived(unidentifiedSubmissions(generationRun).length);
  const pendingCount = $derived(projectsNeedingRecovery(generationRun).length + uncertainCount);
  const label = $derived(sessionPersistence.isSaving ? 'Saving…' : sessionPersistence.error ? 'Save failed' :
    sessionPersistence.savingPaused ? 'Saving paused' : sessionPersistence.savedAt ? 'Saved on device' : 'Sessions');

  function bytes(value) {
    return value == null ? 'Unavailable' : `${(value / 1024 / 1024).toFixed(1)} MB`;
  }

  async function open() {
    dialog.showModal();
    await refreshSessionStorage(listLimit);
  }

  async function resume(id) {
    isBusy = true;
    try {
      if (await restoreSavedSession(sessionPersistence.accountId, id)) dialog.close();
    } finally { isBusy = false; }
  }

  async function remove(id) {
    isBusy = true;
    try { await forgetSavedSession(id); confirmingDelete = null; }
    finally { isBusy = false; }
  }
</script>

{#if sessionPersistence.accountId}
  <button class="storage-button" class:failed={!!sessionPersistence.error} onclick={open}>{label}</button>
{/if}
{#if sessionPersistence.error || sessionPersistence.recoveryError}
  <div class="storage-alert" role="alert">
    <span>{sessionPersistence.error || sessionPersistence.recoveryError}</span>
    <button onclick={() => sessionPersistence.error ? retrySessionSave() : refreshSavedProjects()}
      disabled={sessionPersistence.isSaving || sessionPersistence.isRecovering}>Try again</button>
  </div>
{/if}

<dialog bind:this={dialog} aria-labelledby="session-storage-title" onclose={() => confirmingDelete = null}>
  <div class="dialog-heading">
    <h2 id="session-storage-title">Saved sessions</h2>
    <button class="close-button" onclick={() => dialog.close()} aria-label="Close saved sessions">✕</button>
  </div>
  <p>Sessions, original images, and completed images are saved on this device for your account.</p>
  {#if sessionPersistence.error}<p class="error" role="alert">{sessionPersistence.error}</p>{/if}
  <div class="storage-summary">
    <span>Device storage: {bytes(sessionPersistence.storageUsage)}{#if sessionPersistence.storageQuota != null} of {bytes(sessionPersistence.storageQuota)}{/if}</span>
    {#if sessionPersistence.persistentStorage === false}<span>Your browser may clear device storage. Download images and videos you want to keep.</span>{/if}
  </div>
  <div class="actions">
    <button onclick={retrySessionSave} disabled={sessionPersistence.isSaving || isBusy}>Save current session</button>
    {#if pendingCount > 0}
      <button onclick={refreshSavedProjects} disabled={sessionPersistence.isRecovering || isBusy}>
        {sessionPersistence.isRecovering ? 'Checking projects…' : `Check ${pendingCount} existing project${pendingCount === 1 ? '' : 's'}`}
      </button>
    {/if}
  </div>
  {#if uncertainCount > 0}
    <p class="error" role="status">{uncertainCount} submission{uncertainCount === 1 ? '' : 's'} could not be verified. Check existing projects or your <a href="https://app.sogni.ai" target="_blank" rel="noopener noreferrer">account creations</a> before retrying. Vibe keeps this work saved and waits for confirmation.</p>
  {/if}
  {#if sessionPersistence.recoveryError}<p class="error" role="alert">{sessionPersistence.recoveryError}</p>{/if}
  {#if sessionPersistence.sessions.length === 0}
    <p>No saved sessions yet. Your current work saves automatically when you create.</p>
  {:else}
    <ul>
      {#each sessionPersistence.sessions as saved (saved.id)}
        <li>
          <div class="session-description"><strong>{saved.title}</strong><span>{new Date(saved.updatedAt).toLocaleString()}{saved.id === session.id ? ' · Current' : ''}</span></div>
          {#if confirmingDelete === saved.id}
            <p>Remove this session and its images from device storage? Download anything you want to keep first.</p>
            <div class="actions"><button onclick={() => remove(saved.id)} disabled={isBusy}>Remove saved session</button><button onclick={() => confirmingDelete = null}>Keep it</button></div>
          {:else}
            <div class="actions">
              <button onclick={() => resume(saved.id)} disabled={isBusy || (pendingCount > 0 && saved.id !== session.id)}>Resume</button>
              <button onclick={() => confirmingDelete = saved.id} disabled={isBusy || (saved.id === session.id && pendingCount > 0)}>Remove</button>
            </div>
          {/if}
        </li>
      {/each}
    </ul>
    {#if sessionPersistence.sessions.length === listLimit}
      <button onclick={() => { listLimit += 50; refreshSessionStorage(listLimit); }}>Show more sessions</button>
    {/if}
  {/if}
  <p class="footnote">Removing a saved session leaves your Gallery favorites and their videos in place.</p>
</dialog>

<style>
  button { font: inherit; cursor: pointer; min-height: 44px; padding: 8px 12px; border: 1px solid var(--color-border); border-radius: 8px; background: var(--color-surface); color: var(--color-text); }
  button:disabled { opacity: .5; cursor: default; }
  button:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
  .storage-button { font-size: .8rem; white-space: nowrap; }
  .failed, .error { color: var(--color-error, #ff7878); }
  .storage-alert { position: fixed; bottom: 14px; left: 14px; right: 14px; z-index: 10000; display: flex; gap: 12px; align-items: center; padding: 12px; border: 1px solid var(--color-error); border-radius: 10px; background: var(--color-surface); color: var(--color-text); }
  .storage-alert span { flex: 1; }
  dialog { color: var(--color-text); background: var(--color-bg); border: 1px solid var(--color-border); border-radius: 16px; width: min(580px, calc(100vw - 32px)); max-height: calc(100dvh - 32px); margin: auto; padding: 20px; overflow: auto; }
  dialog::backdrop { background: #0009; }
  .dialog-heading, .actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .dialog-heading { justify-content: space-between; }
  h2 { margin: 0; font-size: 1.3rem; }
  .close-button { flex-shrink: 0; }
  .storage-summary, .session-description { display: flex; flex-direction: column; gap: 6px; margin: 14px 0; }
  .storage-summary, .session-description span, .footnote { color: var(--color-text-muted); font-size: .85rem; }
  ul { padding: 0; list-style: none; }
  li { padding: 12px 0; border-top: 1px solid var(--border-primary, #555); }
  strong { overflow-wrap: anywhere; }
</style>
